import React from "react";
import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  CheckCircle2,
  Star,
  Users,
  TrendingUp,
  Award,
  BookOpen,
  Brain,
  MapPin,
  Phone,
  Clock,
  Zap,
  Shield,
  Target,
  ChevronRight,
  GraduationCap,
  ArrowRight,
  FileText,
  BarChart2,
  ClipboardList,
  Trophy,
} from "lucide-react";
import FAQAccordion from "../components/FAQAccordion";

/* ─── constants ─────────────────────────────────────────────────────── */
const PHONE = "+919918519991";
const WA_MSG = encodeURIComponent(
  "Hello SSS Achievers, I found you while searching for Class 10 coaching in Ghazipur. Please share Class 10 course details. Thank you."
);
const WA_URL = `https://wa.me/919918519991?text=${WA_MSG}`;

const fade = {
  hidden: { opacity: 0, y: 20 },
  visible: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { delay: i * 0.1, duration: 0.5 },
  }),
};

/* ─── data ──────────────────────────────────────────────────────────── */
const STATS = [
  { value: "340+", label: "Students Enrolled", icon: Users },
  { value: "95%", label: "Class 10 Board Success", icon: TrendingUp },
  { value: "90%+", label: "Avg. Board Score", icon: Trophy },
  { value: "15+", label: "Expert Educators", icon: GraduationCap },
];

const CLASS10_SUBJECTS = [
  {
    subject: "Mathematics",
    icon: "📐",
    topics: "Real Numbers, Polynomials, Pair of Linear Equations, Quadratic Equations, AP, Triangles, Circles, Trigonometry, Statistics, Probability & more",
    color: "bg-blue-50 border-blue-200",
  },
  {
    subject: "Science",
    icon: "🔬",
    topics: "Chemical Reactions, Acids & Bases, Metals, Carbon, Periodic Table, Life Processes, Control & Coordination, Heredity, Light, Electricity, Magnetic Effects & more",
    color: "bg-emerald-50 border-emerald-200",
  },
  {
    subject: "Social Science",
    icon: "🗺️",
    topics: "History: Nationalism; Geography: Resources; Civics: Power Sharing, Federalism, Gender; Economics: Development, Consumer Rights & more",
    color: "bg-amber-50 border-amber-200",
  },
  {
    subject: "English",
    icon: "📖",
    topics: "First Flight, Footprints — comprehension, grammar, letter/article writing, literature analysis, board exam writing skills & more",
    color: "bg-violet-50 border-violet-200",
  },
  {
    subject: "Hindi",
    icon: "🖊️",
    topics: "Kshitij 2, Kritika 2 — Gadya/Padya, vyakaran, writing, CBSE board format practice & more",
    color: "bg-rose-50 border-rose-200",
  },
];

const BOARD_STRATEGY = [
  { icon: Brain, title: "NCERT Mastery First", desc: "CBSE board questions are primarily from NCERT. We ensure 100% NCERT coverage for all Class 10 subjects before attempting extra questions." },
  { icon: FileText, title: "10 Years PYQ Analysis", desc: "We analyse the last 10 years of CBSE Class 10 board papers to identify high-weight topics, question patterns and commonly tested concepts." },
  { icon: ClipboardList, title: "Board Answer Writing", desc: "Students are trained to write answers exactly as CBSE examiners expect — with proper structure, keywords, diagrams and step-by-step solutions." },
  { icon: BarChart2, title: "Full Mock Tests", desc: "3-hour full board mock tests as per CBSE marking scheme — followed by detailed analysis and individual feedback to fix weak areas." },
  { icon: Target, title: "Chapter-wise Tests", desc: "Weekly chapter-wise tests ensure consistent revision. No topic is left unrevised before the board exam." },
  { icon: Zap, title: "Speed & Time Management", desc: "Timed practice and exam strategies help students complete the 3-hour board paper comfortably with time for revision." },
  { icon: Shield, title: "Last-Mile Revision", desc: "Structured 45-day board exam revision plan with quick notes, formula sheets and topic-wise priority lists." },
  { icon: Award, title: "Dedicated Doubt Clearing", desc: "Board exam doubts can make students panic. Our dedicated doubt sessions ensure no concept remains unclear before D-day." },
];

const TESTIMONIALS = [
  {
    name: "Pooja Yadav",
    cls: "Class 10 CBSE, 2025",
    rating: 5,
    text: "SSS Achievers is the best Class 10 coaching in Ghazipur! Their board mock tests and answer-writing sessions were game-changers. I scored 93% in CBSE boards. Truly amazing!",
  },
  {
    name: "Shubham Singh",
    cls: "Class 10 CBSE, 2025",
    rating: 5,
    text: "I was scoring 65% before joining SSS Achievers. Their 10-year PYQ analysis and Maths problem practice helped me score 89% in boards. Best Class 10 coaching in Ghazipur!",
  },
  {
    name: "Kavita Tiwari (Parent)",
    cls: "Parent of Class 10 Topper, 2025",
    rating: 5,
    text: "My son scored 95% in CBSE Class 10 boards after coaching at SSS Achievers. The faculty's dedication and personalised approach are second to none in Ghazipur.",
  },
  {
    name: "Divya Verma",
    cls: "Class 10 → Class 11, 2025",
    rating: 5,
    text: "The structured revision plan at SSS Achievers before boards was incredibly effective. I scored 91% and secured Science stream admission. Highly recommend SSS Achievers!",
  },
];

const COMPARISON = [
  { feature: "NCERT Coverage", sss: "100% complete", others: "Partial or selective" },
  { feature: "PYQ Analysis", sss: "10 years of board papers", others: "1–2 years at best" },
  { feature: "Board Mock Tests", sss: "Full 3-hour mocks + analysis", others: "Short quizzes only" },
  { feature: "Answer Writing Training", sss: "Dedicated CBSE format sessions", others: "Not systematically done" },
  { feature: "Batch Size", sss: "Small Batch (focused)", others: "50+ students (crowded)" },
  { feature: "Last-Mile Revision", sss: "Structured 45-day plan", others: "Self-managed" },
  { feature: "Demo Class", sss: "Free before enrollment", others: "Usually not offered" },
];

/* ─── component ─────────────────────────────────────────────────────── */
export default function Class10CoachingGhazipur() {
  return (
    <>
      <Helmet>
        <title>Class 10 Coaching in Ghazipur | Best Class 10 Board Prep – SSS Achievers</title>
        <meta
          name="description"
          content="Best Class 10 coaching in Ghazipur at SSS Achievers. Expert CBSE board preparation, full mock tests, answer writing training, 95% success rate. Free demo class. Enroll today!"
        />
        <meta
          name="keywords"
          content="class 10 coaching in Ghazipur, class 10 tuition Ghazipur, best class 10 coaching Ghazipur, CBSE class 10 coaching Ghazipur, class 10 board preparation Ghazipur, class X coaching Ghazipur UP, SSS Achievers class 10, class 10 maths science Ghazipur, class 10 coaching near me Ghazipur, board exam coaching Ghazipur"
        />
        <link rel="canonical" href="https://www.sssachievers.com/class-10-coaching-ghazipur" />
        <meta property="og:type" content="website" />
        <meta property="og:title" content="Class 10 Coaching in Ghazipur | Best Board Prep – SSS Achievers" />
        <meta property="og:description" content="Expert Class 10 CBSE board coaching in Ghazipur. Full mock tests, answer writing training, 95% success rate. Free demo at SSS Achievers." />
        <meta property="og:url" content="https://www.sssachievers.com/class-10-coaching-ghazipur" />
        <meta property="og:image" content="https://www.sssachievers.com/logo512.png" />
        <meta property="og:locale" content="en_IN" />
        <meta property="og:site_name" content="SSS Achievers" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Class 10 Coaching in Ghazipur | SSS Achievers" />
        <meta name="twitter:description" content="Best Class 10 CBSE board coaching in Ghazipur. 95% success rate, free demo class at SSS Achievers." />
        <meta name="twitter:image" content="https://www.sssachievers.com/logo512.png" />
        <meta name="geo.region" content="IN-UP" />
        <meta name="geo.placename" content="Ghazipur, Uttar Pradesh" />
        <script type="application/ld+json">{`
          {
            "@context": "https://schema.org",
            "@graph": [
              {
                "@type": "EducationalOrganization",
                "@id": "https://www.sssachievers.com/#organization",
                "name": "SSS Achievers",
                "alternateName": ["Best Class 10 Coaching Ghazipur", "Class 10 Board Coaching Ghazipur"],
                "description": "SSS Achievers is the best Class 10 coaching institute in Ghazipur for CBSE board preparation. Expert faculty, full mock tests, 95% success rate.",
                "url": "https://www.sssachievers.com",
                "logo": "https://www.sssachievers.com/logo512.png",
                "telephone": "+919918519991",
                "address": {
                  "@type": "PostalAddress",
                  "streetAddress": "96/B, Badi Bagh",
                  "addressLocality": "Ghazipur",
                  "addressRegion": "Uttar Pradesh",
                  "postalCode": "233001",
                  "addressCountry": "IN"
                },
                "geo": {
                  "@type": "GeoCoordinates",
                  "latitude": 25.5825,
                  "longitude": 83.5805
                },
                "aggregateRating": {
                  "@type": "AggregateRating",
                  "ratingValue": "4.9",
                  "reviewCount": "340",
                  "bestRating": "5"
                }
              },
              {
                "@type": "FAQPage",
                "mainEntity": [
                  {
                    "@type": "Question",
                    "name": "Which is the best Class 10 coaching in Ghazipur?",
                    "acceptedAnswer": {
                      "@type": "Answer",
                      "text": "SSS Achievers is the best Class 10 coaching institute in Ghazipur. With 100% NCERT coverage, 10-year PYQ analysis, full board mock tests, answer-writing training, small batches and a 95% CBSE board success rate, SSS Achievers is Ghazipur's #1 Class 10 coaching centre."
                    }
                  },
                  {
                    "@type": "Question",
                    "name": "Does SSS Achievers prepare students for CBSE Class 10 board exams?",
                    "acceptedAnswer": {
                      "@type": "Answer",
                      "text": "Yes — Class 10 CBSE board preparation is one of SSS Achievers' strongest specialisations. Our coaching covers 100% NCERT content, 10 years of previous year board papers, full mock tests with CBSE marking scheme, answer-writing training and a structured 45-day revision plan before boards."
                    }
                  },
                  {
                    "@type": "Question",
                    "name": "What subjects does SSS Achievers cover for Class 10?",
                    "acceptedAnswer": {
                      "@type": "Answer",
                      "text": "SSS Achievers covers all core Class 10 CBSE subjects — Mathematics, Science (Physics, Chemistry, Biology), Social Science, English and Hindi — each taught by dedicated subject specialists with board exam focus."
                    }
                  },
                  {
                    "@type": "Question",
                    "name": "Does SSS Achievers conduct full board mock tests for Class 10?",
                    "acceptedAnswer": {
                      "@type": "Answer",
                      "text": "Yes. SSS Achievers conducts full 3-hour CBSE Class 10 board mock tests following the official marking scheme. Each mock test is followed by individual performance analysis, subject-wise feedback and strategies to improve weak areas before the actual boards."
                    }
                  },
                  {
                    "@type": "Question",
                    "name": "Is there a free demo class for Class 10 at SSS Achievers Ghazipur?",
                    "acceptedAnswer": {
                      "@type": "Answer",
                      "text": "Yes — SSS Achievers offers a completely free demo class for Class 10. Students and parents can attend a session to experience teaching quality, meet the faculty and understand the board preparation approach before making any commitment."
                    }
                  },
                  {
                    "@type": "Question",
                    "name": "What is the batch size for Class 10 at SSS Achievers?",
                    "acceptedAnswer": {
                      "@type": "Answer",
                      "text": "Class 10 batches at SSS Achievers are kept intentionally small. This ensures every Class 10 student gets focused individual attention, regular doubt-clearing and personalised feedback — critical for board exam success."
                    }
                  }
                ]
              },
              {
                "@type": "BreadcrumbList",
                "itemListElement": [
                  { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://www.sssachievers.com/" },
                  { "@type": "ListItem", "position": 2, "name": "Class 10 Coaching in Ghazipur", "item": "https://www.sssachievers.com/class-10-coaching-ghazipur" }
                ]
              }
            ]
          }
        `}</script>
      </Helmet>

      <main className="bg-white text-slate-900">
        {/* ══ HERO ══ */}
        <section className="relative overflow-hidden bg-gradient-to-br from-[#0097b2]/8 via-white to-[#fcbf0a]/8 pt-10 pb-14 md:pt-20 md:pb-24">
          <div className="absolute inset-0 pointer-events-none overflow-hidden">
            <div className="absolute -top-32 -right-32 w-72 h-72 bg-[#0097b2]/10 rounded-full blur-3xl" />
            <div className="absolute -bottom-32 -left-32 w-72 h-72 bg-[#fcbf0a]/10 rounded-full blur-3xl" />
          </div>

          <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <nav aria-label="breadcrumb" className="mb-6 flex items-center gap-2 text-sm text-slate-500">
              <Link to="/" className="hover:text-[#0097b2] transition">Home</Link>
              <ChevronRight className="w-4 h-4" />
              <Link to="/courses" className="hover:text-[#0097b2] transition">Courses</Link>
              <ChevronRight className="w-4 h-4" />
              <span className="text-[#0097b2] font-medium">Class 10 Coaching in Ghazipur</span>
            </nav>

            <div className="grid md:grid-cols-2 gap-10 items-center">
              <motion.div initial="hidden" animate="visible" variants={fade} className="space-y-6">
                <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#fcbf0a]/20 border border-[#fcbf0a]/40">
                  <Star className="w-4 h-4 text-amber-500 fill-amber-500" />
                  <span className="text-sm font-semibold text-amber-700">Ghazipur's #1 Class 10 Board Coaching</span>
                </div>

                <h1 className="text-4xl md:text-5xl font-bold leading-tight text-slate-900">
                  Best <span className="text-[#0097b2]">Class 10 Coaching</span> in Ghazipur — Score 90%+ in Boards
                </h1>

                <p className="text-base md:text-lg text-slate-600 max-w-xl leading-relaxed">
                  SSS Achievers is Ghazipur's most trusted Class 10 CBSE board coaching centre. With 100% NCERT coverage, 10-year PYQ analysis, full mock tests, answer-writing training and small focused batches, our Class 10 students consistently score 90%+ in CBSE boards.
                </p>

                <div className="flex flex-col sm:flex-row gap-3">
                  <a href={WA_URL} target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center gap-2 px-6 py-3.5 bg-[#0097b2] text-white font-semibold rounded hover:bg-[#007a91] shadow-md hover:shadow-lg transition">
                    Book Free Demo Class 10 <ArrowRight className="w-4 h-4" />
                  </a>
                  <a href={`tel:${PHONE}`} className="inline-flex items-center justify-center gap-2 px-6 py-3.5 border-2 border-[#0097b2] text-[#0097b2] font-semibold rounded hover:bg-[#0097b2]/5 transition">
                    <Phone className="w-4 h-4" /> Call: +91 99185 19991
                  </a>
                </div>

                <div className="flex flex-wrap gap-4 pt-2">
                  {["100% NCERT Coverage", "Full Board Mock Tests", "Answer Writing Training", "95% Success Rate"].map((t) => (
                    <span key={t} className="inline-flex items-center gap-1.5 text-sm text-slate-600">
                      <CheckCircle2 className="w-4 h-4 text-[#0097b2]" /> {t}
                    </span>
                  ))}
                </div>
              </motion.div>

              <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.6, delay: 0.2 }} className="grid grid-cols-2 gap-4">
                {STATS.map(({ value, label, icon: Icon }) => (
                  <div key={label} className="bg-white rounded-2xl p-6 shadow-md border border-gray-100 flex flex-col items-center text-center hover:shadow-lg transition">
                    <div className="w-12 h-12 rounded-xl bg-[#0097b2]/10 flex items-center justify-center mb-3">
                      <Icon className="w-6 h-6 text-[#0097b2]" />
                    </div>
                    <span className="text-3xl font-bold text-[#0097b2]">{value}</span>
                    <span className="text-sm text-slate-500 mt-1 font-medium">{label}</span>
                  </div>
                ))}
                <div className="col-span-2 bg-gradient-to-r from-[#0097b2] to-[#006f85] rounded-2xl p-5 text-white flex items-center gap-4">
                  <div>
                    <div className="flex gap-1 mb-1">
                      {[1,2,3,4,5].map((s) => <Star key={s} className="w-5 h-5 fill-[#fcbf0a] text-[#fcbf0a]" />)}
                    </div>
                    <p className="font-bold text-lg">4.9 / 5.0 Rating</p>
                    <p className="text-blue-100 text-sm">340+ happy Class 10 students</p>
                  </div>
                  <div className="ml-auto text-right">
                    <p className="text-blue-100 text-xs uppercase tracking-wider font-semibold">Class</p>
                    <p className="text-3xl font-bold">10th</p>
                  </div>
                </div>
              </motion.div>
            </div>
          </div>
        </section>

        {/* ══ URGENCY BANNER ══ */}
        <section className="py-10 bg-gradient-to-r from-[#fcbf0a] to-[#e6ac00]">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div initial={{ opacity: 0, y: 10 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="text-center">
              <h2 className="text-xl md:text-2xl font-bold text-gray-900 mb-2">⚠️ Class 10 CBSE Boards — Your Future Depends on This Year</h2>
              <p className="text-gray-800 text-sm md:text-base max-w-3xl mx-auto">
                Class 10 board percentage determines stream selection for Class 11. Science, Commerce or any competitive stream requires 90%+. SSS Achievers ensures you not only meet that bar but exceed it — with expert coaching right here in Ghazipur.
              </p>
            </motion.div>
          </div>
        </section>

        {/* ══ SUBJECTS ══ */}
        <section className="py-16 md:py-20 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <motion.h2 initial={{ opacity: 0, y: 12 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="text-3xl md:text-4xl font-bold">
                What We Teach in Class 10 at <span className="text-[#0097b2]">SSS Achievers Ghazipur</span>
              </motion.h2>
              <p className="mt-3 text-slate-600">All five CBSE subjects — board exam focused, taught by specialist faculty</p>
            </div>

            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {CLASS10_SUBJECTS.map(({ subject, icon, topics, color }, i) => (
                <motion.div key={subject} initial="hidden" whileInView="visible" viewport={{ once: true, amount: 0.15 }} custom={i} variants={fade}
                  className={`rounded-2xl border-2 ${color} bg-white p-6 hover:shadow-lg transition-all`}>
                  <div className="text-3xl mb-3">{icon}</div>
                  <h3 className="font-bold text-slate-800 text-lg mb-2">Class 10 {subject}</h3>
                  <p className="text-slate-500 text-sm leading-relaxed">{topics}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* ══ BOARD STRATEGY ══ */}
        <section className="py-16 md:py-20 bg-gradient-to-br from-[#0097b2]/5 to-[#fcbf0a]/5">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <motion.h2 initial={{ opacity: 0, y: 12 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="text-3xl md:text-4xl font-bold">
                Our Proven <span className="text-[#0097b2]">Class 10 Board Exam Strategy</span> at SSS Achievers
              </motion.h2>
              <p className="mt-4 text-slate-600 max-w-2xl mx-auto text-base">An 8-step system that consistently delivers 90%+ board results for Class 10 students in Ghazipur.</p>
            </div>

            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {BOARD_STRATEGY.map(({ icon: Icon, title, desc }, i) => (
                <motion.div key={title} initial="hidden" whileInView="visible" viewport={{ once: true, amount: 0.15 }} custom={i} variants={fade}
                  className="group p-6 rounded-2xl border-2 border-gray-100 hover:border-[#0097b2]/40 hover:shadow-lg transition-all duration-300 bg-white">
                  <div className="w-12 h-12 rounded-xl bg-[#0097b2]/10 flex items-center justify-center mb-4 group-hover:bg-[#0097b2]/20 transition-colors">
                    <Icon className="w-6 h-6 text-[#0097b2]" />
                  </div>
                  <h3 className="font-bold text-slate-800 mb-2 text-base">{title}</h3>
                  <p className="text-slate-500 text-sm leading-relaxed">{desc}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* ══ COMPARISON ══ */}
        <section className="py-16 md:py-20 bg-white">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <motion.h2 initial={{ opacity: 0, y: 12 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="text-3xl md:text-4xl font-bold">
                SSS Achievers Class 10 vs. Other Coaching Centres in Ghazipur
              </motion.h2>
            </div>
            <div className="overflow-hidden rounded-2xl border-2 border-gray-100 shadow-sm">
              <div className="grid grid-cols-3 bg-[#0097b2] text-white text-sm font-bold">
                <div className="px-5 py-4">Feature</div>
                <div className="px-5 py-4 text-center border-l border-white/20">SSS Achievers ✓</div>
                <div className="px-5 py-4 text-center border-l border-white/20 text-blue-100">Others</div>
              </div>
              {COMPARISON.map(({ feature, sss, others }, i) => (
                <div key={feature} className={`grid grid-cols-3 text-sm border-b border-gray-100 ${i % 2 === 0 ? "bg-white" : "bg-gray-50"}`}>
                  <div className="px-5 py-4 font-semibold text-slate-700">{feature}</div>
                  <div className="px-5 py-4 text-center border-l border-gray-100">
                    <div className="flex items-center justify-center gap-2">
                      <CheckCircle2 className="w-4 h-4 text-[#0097b2] flex-shrink-0" />
                      <span className="text-slate-700 font-medium">{sss}</span>
                    </div>
                  </div>
                  <div className="px-5 py-4 text-center border-l border-gray-100 text-slate-400 text-xs">{others}</div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ══ TESTIMONIALS ══ */}
        <section className="py-16 md:py-20 bg-gradient-to-br from-[#0097b2]/5 to-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <motion.h2 initial={{ opacity: 0, y: 12 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="text-3xl md:text-4xl font-bold">
                Class 10 Toppers Who Trained at <span className="text-[#0097b2]">SSS Achievers Ghazipur</span>
              </motion.h2>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {TESTIMONIALS.map(({ name, cls, rating, text }, i) => (
                <motion.div key={name} initial="hidden" whileInView="visible" viewport={{ once: true, amount: 0.15 }} custom={i} variants={fade}
                  className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100 hover:shadow-md transition flex flex-col">
                  <div className="flex gap-0.5 mb-3">
                    {Array.from({ length: rating }).map((_, s) => <Star key={s} className="w-4 h-4 fill-[#fcbf0a] text-[#fcbf0a]" />)}
                  </div>
                  <p className="text-slate-600 text-sm leading-relaxed flex-1 italic">"{text}"</p>
                  <div className="mt-4 pt-4 border-t border-gray-100">
                    <p className="font-semibold text-slate-800 text-sm">{name}</p>
                    <p className="text-xs text-slate-400 mt-0.5">{cls}</p>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* ══ CAMPUS GLIMPSE ══ */}
        <section className="py-14 md:py-16 bg-slate-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-8">
              <motion.h2
                initial={{ opacity: 0, y: 12 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                className="text-2xl md:text-3xl font-bold"
              >
                Class 10 Board Results & <span className="text-[#0097b2]">Our Campus — SSS Achievers Ghazipur</span>
              </motion.h2>
              <p className="mt-2 text-slate-500 text-sm">Proof of results. Walk-in and see for yourself.</p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              {[
                {
                  src: "/images/sss%20board%20result%20award%20ceremony.avif",
                  alt: "SSS Achievers Class 10 CBSE board result award ceremony in Ghazipur — students receiving excellence awards for top board exam scores in 2025",
                  caption: "🏆 Class 10 Board Result Award Ceremony 2025",
                },
                {
                  src: "/images/sss%20board%20result%20celebration.avif",
                  alt: "SSS Achievers Class 10 CBSE students celebrating board exam results in Ghazipur — top scorers at the best Class 10 coaching centre in Ghazipur UP",
                  caption: "🎉 Class 10 Toppers Celebrating — Ghazipur",
                },
                {
                  src: "/images/gallery/mock-test.jpg",
                  alt: "SSS Achievers Class 10 CBSE board mock test session in Ghazipur — students appearing in full 3-hour board mock exam at the best Class 10 coaching centre in Ghazipur",
                  caption: "📋 Class 10 Board Mock Test — SSS Achievers",
                },
              ].map(({ src, alt, caption }, i) => (
                <motion.figure
                  key={caption}
                  initial={{ opacity: 0, y: 16 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.1 }}
                  className="rounded-2xl overflow-hidden shadow-md group"
                >
                  <img
                    src={src}
                    alt={alt}
                    className="w-full h-56 object-cover group-hover:scale-105 transition-transform duration-500"
                    loading="lazy"
                    width="600"
                    height="224"
                  />
                  <figcaption className="bg-white px-4 py-3 text-sm font-semibold text-slate-700 border-t border-gray-100">
                    {caption}
                  </figcaption>
                </motion.figure>
              ))}
            </div>
          </div>
        </section>

        {/* ══ LOCATION ══ */}
        <section className="py-16 md:py-20 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid md:grid-cols-2 gap-12 items-center">
              <motion.div initial={{ opacity: 0, x: -20 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} className="space-y-5">
                <h2 className="text-3xl md:text-4xl font-bold">
                  Enroll for <span className="text-[#0097b2]">Class 10 Coaching in Ghazipur</span> Today
                </h2>
                <p className="text-slate-600">Seats are limited per batch. Visit SSS Achievers or book via WhatsApp for your Class 10 CBSE board journey.</p>
                {[
                  { icon: MapPin, text: "96/B, Badi Bagh, Opp. Raj Nursing Home, Ghazipur, UP – 233001" },
                  { icon: Phone, text: "+91 99185 19991", href: `tel:${PHONE}` },
                  { icon: Clock, text: "Mon–Sat: 9:00 AM – 7:00 PM" },
                ].map(({ icon: Icon, text, href }) => (
                  <div key={text} className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-lg bg-[#0097b2]/10 flex items-center justify-center flex-shrink-0">
                      <Icon className="w-5 h-5 text-[#0097b2]" />
                    </div>
                    {href ? <a href={href} className="text-sm text-[#0097b2] font-semibold hover:underline mt-2">{text}</a> : <p className="text-sm text-slate-600 mt-2">{text}</p>}
                  </div>
                ))}
                <div className="flex flex-wrap gap-3 pt-2">
                  <Link to="/courses/class-10-board-preparation" onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })} className="inline-flex items-center gap-2 px-5 py-3 bg-[#0097b2] text-white font-semibold rounded hover:bg-[#007a91] transition shadow-md text-sm">
                    View Class 10 Course <ArrowRight className="w-4 h-4" />
                  </Link>
                  <Link to="/class-9-coaching-ghazipur" onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })} className="inline-flex items-center gap-2 px-5 py-3 border-2 border-[#0097b2] text-[#0097b2] font-semibold rounded hover:bg-[#0097b2]/5 transition text-sm">
                    Also See Class 9
                  </Link>
                </div>
              </motion.div>

              <motion.div initial={{ opacity: 0, x: 20 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} className="rounded-3xl overflow-hidden border-2 border-gray-100 shadow-lg">
                <div className="bg-[#0097b2] px-6 py-4 flex items-center gap-3">
                  <MapPin className="w-5 h-5 text-white" />
                  <span className="text-white font-semibold">SSS Achievers — Ghazipur</span>
                </div>
                <iframe
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3598.750962143519!2d83.56063587575478!3d25.57995091609078!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x2bb0bb23b08ac59%3A0x4c04724e0b4e6039!2sSSS%20ACHIEVERS%20Coaching%20Centre%20Ghazipur!5e0!3m2!1sen!2sin!4v1775367597808!5m2!1sen!2sin"
                  width="100%"
                  height="280"
                  style={{ border: 0 }}
                  allowFullScreen=""
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title="SSS Achievers Class 10 Coaching Ghazipur Location"
                />
              </motion.div>
            </div>
          </div>
        </section>

        {/* ══ FAQ ══ */}
        <section className="py-16 md:py-20 bg-gradient-to-br from-[#0097b2]/5 to-white">
          <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-10">
              <motion.h2 initial={{ opacity: 0, y: 12 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="text-3xl md:text-4xl font-bold">Frequently Asked Questions</motion.h2>
              <p className="mt-3 text-slate-500 text-sm">About Class 10 coaching in Ghazipur at SSS Achievers</p>
            </div>
            <FAQAccordion
              faqs={[
                { q: "Which is the best Class 10 coaching in Ghazipur?", a: "SSS Achievers is the best Class 10 coaching institute in Ghazipur. With 100% NCERT coverage, 10-year PYQ analysis, full board mock tests, answer-writing training, small, focused batches and a 95% CBSE board success rate, SSS Achievers is Ghazipur's #1 Class 10 coaching centre." },
                { q: "How does SSS Achievers prepare students for CBSE Class 10 boards?", a: "SSS Achievers uses an 8-step board strategy: 100% NCERT mastery, 10-year PYQ analysis, answer-writing training, full 3-hour mock tests, chapter-wise weekly tests, speed and time management drills, structured 45-day revision and dedicated doubt-clearing sessions." },
                { q: "What is the average board score of Class 10 students at SSS Achievers?", a: "SSS Achievers Class 10 students consistently score 90%+ in CBSE boards. Our 95% overall success rate reflects the effectiveness of our structured board preparation approach." },
                { q: "Does SSS Achievers conduct full board mock tests for Class 10?", a: "Yes. SSS Achievers conducts full 3-hour CBSE Class 10 board mock tests as per the official marking scheme. Each mock is followed by individual performance analysis, subject-wise feedback and targeted improvement strategies." },
                { q: "What subjects are covered in Class 10 coaching at SSS Achievers?", a: "All Class 10 CBSE subjects are covered — Mathematics, Science (Physics, Chemistry, Biology), Social Science, English and Hindi — each by a dedicated subject specialist with board exam focus." },
                { q: "Is there a free demo class for Class 10 at SSS Achievers Ghazipur?", a: "Yes — SSS Achievers offers a completely free demo class for Class 10. Attend a session to experience our board coaching quality, meet the faculty and understand the full preparation approach before enrolling." },
              ]}
            />
          </div>
        </section>

        {/* ══ FINAL CTA ══ */}
        <section className="py-16 md:py-20 bg-gradient-to-r from-[#0097b2] to-[#006f85] relative overflow-hidden">
          <div className="absolute inset-0 pointer-events-none">
            <div className="absolute -top-16 -right-16 w-64 h-64 bg-white/5 rounded-full blur-3xl" />
            <div className="absolute -bottom-16 -left-16 w-64 h-64 bg-[#fcbf0a]/10 rounded-full blur-3xl" />
          </div>
          <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="space-y-6">
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/10 border border-white/20">
                <Star className="w-4 h-4 text-[#fcbf0a] fill-[#fcbf0a]" />
                <span className="text-white text-sm font-semibold">Ghazipur's #1 Class 10 Board Coaching</span>
              </div>
              <h2 className="text-3xl md:text-4xl font-bold text-white">Score 90%+ in Class 10 CBSE Boards with SSS Achievers</h2>
              <p className="text-blue-100 text-base md:text-lg max-w-2xl mx-auto">
                Limited seats per batch. Don't wait — book your free demo Class 10 coaching session today and secure your spot at Ghazipur's most trusted coaching centre.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <a href={WA_URL} target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-[#fcbf0a] text-gray-900 font-bold rounded hover:bg-[#e6ac00] transition shadow-lg text-base">
                  Book Free Demo Class 10 <ArrowRight className="w-5 h-5" />
                </a>
                <a href={`tel:${PHONE}`} className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-white/10 border-2 border-white/30 text-white font-bold rounded hover:bg-white/20 transition text-base">
                  <Phone className="w-5 h-5" /> +91 99185 19991
                </a>
              </div>
              <p className="text-blue-200 text-xs mt-4">📍 96/B, Badi Bagh, Opp. Raj Nursing Home, Ghazipur, UP – 233001</p>
            </motion.div>
          </div>
        </section>
      </main>
    </>
  );
}
